import React, { useState, useEffect, useContext } from 'react';
import {
  Search,
  Package,
  Edit3,
  Trash2,
  ArrowRight,
  RefreshCw,
  Filter,
  X,
  CheckCircle2,
  AlertTriangle,
  User,
  Calendar,
  Hash,
  Box,
  Move
} from 'lucide-react';
import InboundContext from './InboundContext';
import { Button, Input, Card, Badge, LoadingSpinner, Notification } from './SharedComponents';
import moment from 'moment';

const API_URL = import.meta.env.VITE_API_URL;

function ShelfSearch() {
  // State management
  const { shelvingData, loadShelvingData, selectedNickname } = useContext(InboundContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchableShelvingData, setSearchableShelvingData] = useState([]);
  const [editQtyArray, setEditQtyArray] = useState([]);
  const [initialQtyArray, setInitialQtyArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Dialog states
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferQty, setTransferQty] = useState('');
  const [newShelfCode, setNewShelfCode] = useState('');
  const [transferType, setTransferType] = useState('partial');
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [currentKey, setCurrentKey] = useState(null);
  const [currentQty, setCurrentQty] = useState(0);
  const [transferDetails, setTransferDetails] = useState({
    sku: null,
    teamId: null,
    shelfCode: null,
    qty: null
  });

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // Transform shelving data for search
  useEffect(() => {
    if (shelvingData.length > 0) {
      const transformedData = shelvingData.map(item => {
        const { KEY, ...rest } = item;
        return {
          KEY,
          jsonData: JSON.stringify(rest),
          ...rest
        };
      });
      setSearchableShelvingData(transformedData);
    }
  }, [shelvingData]);

  // Filter results based on search
  useEffect(() => {
    if (searchTerm.length > 2) {
      const results = searchableShelvingData.filter(item =>
        item.jsonData.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResults(results);
      setEditQtyArray(new Array(results.length).fill(''));
      setInitialQtyArray(results.map(item => item.QTY));
    } else {
      setFilteredResults([]);
      setEditQtyArray([]);
      setInitialQtyArray([]);
    }
  }, [searchTerm, searchableShelvingData]);

  // Handle quantity update
  const handleQuantityUpdate = async (index) => {
    const item = filteredResults[index];
    const newQty = parseInt(editQtyArray[index]) || 0;

    if (newQty === item.QTY) {
      showNotification('Quantity unchanged', 'info');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: item.KEY,
          shelfCode: item.FULL_SHELF_CODE,
          qty: newQty,
          employee: selectedNickname || 'SYSTEM',
          sku: item.SKU,
          action_type: "INBOUND_TOOL_SHELFSEARCH_EDITQTY",
          shipmentId: item.INBOUND_ID || 'N/A',
          team_id: item.TEAM_ID
        })
      });

      const result = await response.json();
      if (result.success) {
        showNotification(newQty === 0 ? 'Item removed from shelf' : 'Quantity updated successfully');
        loadShelvingData();

        // Update local state
        const updatedResults = [...filteredResults];
        if (newQty === 0) {
          updatedResults.splice(index, 1);
          setFilteredResults(updatedResults);
        } else {
          updatedResults[index].QTY = newQty;
          setFilteredResults(updatedResults);
        }
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      showNotification('Failed to update quantity', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle transfer dialog
  const handleTransferDialog = (item) => {
    setCurrentKey(item.KEY);
    setCurrentQty(item.QTY);
    setTransferDetails({
      sku: item.SKU,
      teamId: item.TEAM_ID,
      shelfCode: item.FULL_SHELF_CODE,
      qty: item.QTY
    });
    setTransferDialogOpen(true);
    setTransferQty('');
    setNewShelfCode('');
    setTransferType('partial');
  };

  // Handle transfer
  const handleTransfer = async () => {
    const qty = transferType === 'all' ? currentQty : parseInt(transferQty) || 0;

    if (!newShelfCode || (transferType === 'partial' && qty <= 0)) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    if (transferType === 'partial' && qty > currentQty) {
      showNotification('Transfer quantity cannot exceed available quantity', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/transfer_shelf_qty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromKey: currentKey,
          toShelfCode: newShelfCode,
          transferQty: qty,
          sku: transferDetails.sku,
          employee: selectedNickname || 'SYSTEM',
          fromShelfCode: transferDetails.shelfCode,
          action_type: "INBOUND_TOOL_SHELFSEARCH_TRANSFER",
          team_id: transferDetails.teamId
        })
      });

      const result = await response.json();
      if (result.success) {
        showNotification(`Transferred ${qty} items to shelf ${newShelfCode}`);
        setTransferDialogOpen(false);
        loadShelvingData();
      } else {
        throw new Error('Transfer failed');
      }
    } catch (error) {
      console.error('Error transferring items:', error);
      showNotification('Failed to transfer items', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove confirmation
  const handleRemoveConfirm = (key) => {
    setCurrentKey(key);
    setRemoveConfirmOpen(true);
  };

  const handleRemove = async () => {
    try {
      setIsLoading(true);
      const item = filteredResults.find(item => item.KEY === currentKey);

      const response = await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: currentKey,
          shelfCode: item.FULL_SHELF_CODE,
          qty: 0,
          employee: selectedNickname || 'SYSTEM',
          sku: item.SKU,
          action_type: "INBOUND_TOOL_SHELFSEARCH_REMOVE",
          shipmentId: item.INBOUND_ID || 'N/A',
          team_id: item.TEAM_ID
        })
      });

      const result = await response.json();
      if (result.success) {
        showNotification('Item removed from shelf');
        setRemoveConfirmOpen(false);
        loadShelvingData();

        // Remove from local results
        setFilteredResults(prev => prev.filter(item => item.KEY !== currentKey));
      } else {
        throw new Error('Remove failed');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      showNotification('Failed to remove item', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Shelf Search</h1>
            <p className="text-gray-600">Search and manage items on warehouse shelves</p>
          </div>
          <Button
            onClick={() => loadShelvingData()}
            variant="outline"
            disabled={isLoading}
            className="flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>

        {/* Search Bar */}
        <Card className="p-4">
          <Input
            placeholder="Search by SKU, shelf code, product title, or client name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
            className="text-base"
          />

          {searchTerm.length > 0 && searchTerm.length <= 2 && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Please enter at least 3 characters to search
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Results */}
      {searchTerm.length > 2 && (
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Shelf Items</h2>
              <Badge variant="default">
                {filteredResults.length} items found
              </Badge>
            </div>
          </div>

          {filteredResults.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shelf Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Qty
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredResults.map((item, index) => (
                    <tr key={item.KEY} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          {item.LARGE_IMG ? (
                            <img
                              src={item.LARGE_IMG}
                              alt="Product"
                              className="w-12 h-12 object-cover rounded border border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                              {item.TITLE || 'Untitled Product'}
                            </p>
                            <div className="mt-1 space-y-1">
                              <div className="flex items-center text-xs text-gray-500">
                                <Hash className="w-3 h-3 mr-1" />
                                <span className="font-mono">{item.SKU}</span>
                              </div>
                              {item.ASIN && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="font-mono">{item.ASIN}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Box className="w-4 h-4 mr-2 text-gray-400" />
                          <code className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded font-mono">
                            {item.FULL_SHELF_CODE}
                          </code>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            value={editQtyArray[index] !== '' ? editQtyArray[index] : item.QTY}
                            onChange={(e) => {
                              const newArray = [...editQtyArray];
                              newArray[index] = e.target.value;
                              setEditQtyArray(newArray);
                            }}
                            className="w-20 text-center"
                            min="0"
                          />
                          {editQtyArray[index] !== '' && parseInt(editQtyArray[index]) !== item.QTY && (
                            <Button
                              size="sm"
                              onClick={() => handleQuantityUpdate(index)}
                              disabled={isLoading}
                              className="px-3"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-sm text-gray-900">{item.name || 'N/A'}</span>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-2" />
                          <div>
                            <div>{moment(item.UPDATED).format('MMM D, YYYY')}</div>
                            <div className="text-xs">{moment(item.UPDATED).fromNow()}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTransferDialog(item)}
                            className="p-2"
                            title="Transfer to another shelf"
                          >
                            <Move className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveConfirm(item.KEY)}
                            className="p-2 text-red-600 hover:text-red-700"
                            title="Remove from shelf"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Found</h3>
              <p className="text-gray-500">
                No shelf items match your search criteria. Try different keywords.
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Empty State */}
      {searchTerm.length === 0 && (
        <div className="text-center py-20">
          <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <Search className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">Search Shelf Items</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Enter a search term above to find items on warehouse shelves and manage their quantities or locations.
          </p>
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Edit quantities
            </div>
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Transfer between shelves
            </div>
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Remove items
            </div>
          </div>
        </div>
      )}

      {/* Transfer Dialog */}
      {transferDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Transfer Items</h3>
                <Button
                  variant="ghost"
                  onClick={() => setTransferDialogOpen(false)}
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="transferType"
                        value="partial"
                        checked={transferType === 'partial'}
                        onChange={(e) => setTransferType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Partial transfer</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="transferType"
                        value="all"
                        checked={transferType === 'all'}
                        onChange={(e) => setTransferType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Transfer all ({currentQty} items)</span>
                    </label>
                  </div>
                </div>

                {transferType === 'partial' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity to Transfer (max: {currentQty})
                    </label>
                    <Input
                      type="number"
                      value={transferQty}
                      onChange={(e) => setTransferQty(e.target.value)}
                      min="1"
                      max={currentQty}
                      placeholder="Enter quantity"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Shelf Code
                  </label>
                  <Input
                    value={newShelfCode}
                    onChange={(e) => setNewShelfCode(e.target.value.toUpperCase())}
                    placeholder="e.g., P-02-03"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setTransferDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTransfer}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Transfer Items
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Remove Confirmation Dialog */}
      {removeConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Remove Item</h3>
              </div>

              <p className="text-gray-600 mb-6">
                Are you sure you want to remove this item from the shelf? This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setRemoveConfirmOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemove}
                  disabled={isLoading}
                  variant="danger"
                  className="flex-1"
                >
                  Remove Item
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ShelfSearch;